/**
 * The address keys of an offline-queued capture (damage assessment, road closure,
 * hazard), as the CREATE call sends them after sync.
 *
 * WHY THIS EXISTS (map-first capture, migration 680): an address is a label, the
 * point is the record. When a record has a point and NO address, the server fills
 * the address from the point after sync (record_reverse_geocode). An empty string
 * is not "no address" on the wire: the device used to queue `address: ''`, so a
 * record captured offline reached the server with a blank address instead of none.
 * A typed address must keep its 'entered' marker, which is what tells the server
 * never to overwrite it with a geocoded one.
 *
 * Rules (create only; an edit keeps sending what the person set):
 *   - blank / missing address  -> neither `address` nor `address_source` is sent;
 *   - non-blank address        -> the trimmed address, plus its source when it is a
 *                                 known one ('entered' | 'reverse_geocode').
 */
export type OfflineAddressSource = 'entered' | 'reverse_geocode';

export function isOfflineAddressSource(value: unknown): value is OfflineAddressSource {
  return value === 'entered' || value === 'reverse_geocode';
}

export function normalizeCreateAddressKeys<T extends Record<string, unknown>>(payload: T): T {
  const out: Record<string, unknown> = { ...payload };
  const address = typeof out.address === 'string' ? out.address.trim() : '';
  if (!address) {
    delete out.address;
    delete out.address_source;
    return out as T;
  }
  out.address = address;
  if (!isOfflineAddressSource(out.address_source)) delete out.address_source;
  return out as T;
}
